import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import API from 'api';
import {
  setActivityPartial,
  setActivityMastered,
} from 'actions/flinkPlayActions';
import { ActivityButtons, CharacterAnimation, WhiteBox } from 'components/flink-play';
import { printElem, getScale } from 'utils';
import baseResolution from 'consts/base-resolution';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { GO_BACK, SPEAK, HELP, PRINT } from 'consts/buttons';
import { activitiesKey, usersKey, uploadsURL, imagesURL } from 'config';

import { stringProblemsMapper } from 'activity-templates/utils';
import { toggleAudio, stopAudio } from 'actions/audioActions';
import { closeWritingFeedback } from 'actions/flinkPlayActions';
import { showStats } from 'actions/gameActions';

import { EDITOR_WIDTH } from './writingAssistantHelpers';
import WritingAssistantEditor from './WritingAssistantEditor/WritingAssistantEditor';
import GuideBox from './GuideBox/GuideBox';
import { WRITING_ASSISTANT } from 'consts/activity-templates';
import classes from './WritingAssistant.module.scss';
import './mobile/WritingAssistant.mobile.scss';
import './mobile/WritingAssistant.android.scss';
import './mobile/WritingAssistant.ios.scss';

import { WritingFeedback } from 'components/flink-play';

const templateGraphicsFolder = `${imagesURL}/TemplateGraphics/WritingAssistant/`;

const divideSize = (s) => s / 2 + 'px';

const WRITING_BASE_WIDTH = 1000;
const WRITING_PHONE_SCALE_MULTIPLIER = 1.1;
const WRITING_PHONE_MAX_SCALE = 0.72;
const WRITING_PHONE_HEIGHT = 1040;
const WRITING_PHONE_EDITOR_WIDTH = 1640;
const WRITING_PHONE_RENDER_HEIGHT = WRITING_PHONE_HEIGHT / 2;
const WRITING_ANDROID_BOTTOM_RESERVE_VMIN = 13;
const WRITING_IOS_BOTTOM_RESERVE_VMIN = 13;
const WRITING_ANDROID_EDGE_GAP_PX = 6;
const WRITING_IOS_EDGE_GAP_PX = 6;

const getViewportSize = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };

  const visualViewport = window.visualViewport;
  return {
    width: Math.round(visualViewport?.width || window.innerWidth || 0),
    height: Math.round(visualViewport?.height || window.innerHeight || 0),
  };
};

const isNativePhoneViewport = (mobileLayout = false, viewportOverride = null) => {
  if (!mobileLayout || typeof window === 'undefined') return false;

  const viewport = viewportOverride || getViewportSize();
  const shortSide = Math.min(viewport.width || 0, viewport.height || 0);
  return shortSide > 0 && shortSide <= 700;
};

const getScaleForViewport = (viewport) => {
  if (!viewport?.width || !viewport?.height) return getScale();

  const baseWidth = baseResolution.width / 2;
  const baseHeight = baseResolution.height / 2;
  return Math.min(viewport.width / baseWidth, viewport.height / baseHeight);
};

const getMobileLayoutConfig = (platform) => (
  platform === 'ios'
    ? {
      bottomReserveVmin: WRITING_IOS_BOTTOM_RESERVE_VMIN,
      edgeGapPx: WRITING_IOS_EDGE_GAP_PX,
    }
    : {
      bottomReserveVmin: WRITING_ANDROID_BOTTOM_RESERVE_VMIN,
      edgeGapPx: WRITING_ANDROID_EDGE_GAP_PX,
    }
);

const getWritingScale = (mobileLayout = false, viewportOverride = null, platform = 'android') => {
  const viewport = viewportOverride || getViewportSize();
  const defaultScale = viewportOverride ? getScaleForViewport(viewport) : getScale();

  if (!isNativePhoneViewport(mobileLayout, viewport)) return defaultScale;

  const widthScale = Math.max(0, (viewport.width - 12) / WRITING_BASE_WIDTH);
  const { bottomReserveVmin, edgeGapPx } = getMobileLayoutConfig(platform);
  const vmin = Math.min(viewport.width, viewport.height) / 100;
  const reservedBottom = bottomReserveVmin * vmin;
  const availableHeight = Math.max(
    0,
    viewport.height - reservedBottom - edgeGapPx * 2,
  );
  const verticalFitScale = availableHeight / WRITING_PHONE_RENDER_HEIGHT;

  return Math.max(0.32, Math.min(
    WRITING_PHONE_MAX_SCALE,
    widthScale,
    defaultScale * WRITING_PHONE_SCALE_MULTIPLIER,
    verticalFitScale,
  ));
};

const getWritingTop = (viewport, platform, scale) => {
  if (!viewport?.width || !viewport?.height) return undefined;

  const { bottomReserveVmin, edgeGapPx } = getMobileLayoutConfig(platform);
  const vmin = Math.min(viewport.width, viewport.height) / 100;
  const safeBottom = viewport.height - bottomReserveVmin * vmin - edgeGapPx;
  const wrapperVisualHeight = WRITING_PHONE_RENDER_HEIGHT * scale;
  const minCenter = edgeGapPx + wrapperVisualHeight / 2;
  const maxCenter = safeBottom - wrapperVisualHeight / 2;
  const availableCenter = (edgeGapPx + safeBottom) / 2;
  const center = Math.max(minCenter, Math.min(availableCenter, maxCenter));

  return `${Math.round(center * 10) / 10}px`;
};

const countWritingSentences = (text = '') => String(text || '')
  .replace(/\u00a0/g, ' ')
  .split(/(?:[.!?。！？]+|\n+)/u)
  .map((part) => part.trim())
  .filter(Boolean)
  .length;

const defaultData = {
  templateAlias: WRITING_ASSISTANT,
  text: '',
};

export class WritingAssistant extends Component {
  mobilePlatform = getMobileAppPlatform();

  mobileStableViewport = null;

  mobileViewportTimer = null;


  exitResult = null;

  constructor(props) {
    super(props);

    const { activity, learnerId, team, translate, getStringAudio } = props;
    
    let locale = props.activity.activity?.monolingual ? '' 
      : (window.location.href.indexOf('edit-activities') !== -1 &&
        props.activity.activity?.currentLanguageVersion?.locale 
      ? props.activity.activity.currentLanguageVersion.locale
      : props?.solutionLocale?.code ? props.solutionLocale.code : '');
    const versionType = (!locale || locale === 'en') ? "" : `immersion_${locale}`;
    
    const gameData = versionType 
    ? props.activity.data.languageVersionData?.[versionType]
    : props.activity.data.gameData;
    console.log('WritingAssistant locale: ',locale);
    const {
      /*data: {
        gameData: {
          guides = [],
          images = [],
          words = [],
          starters = [],
          reading = '',
          multiLocaleGuides,
        },
      },*/
      activity: { _id: activityId },
    } = activity;

    const {
          guides = [],
          images = [],
          words = [],
          starters = [],
          reading = '',
          multiLocaleGuides,
        } = gameData;
    
    const learnersIDs = [];

    if (learnerId) {
      learnersIDs.push(learnerId);
    }

    if (team) {
      learnersIDs.push(...team.map((l) => l._id));
    }

    const activityAssetsKey = versionType ? `${activitiesKey}/${activityId}/languageVersionData/${versionType}` 
    : `${activitiesKey}/${activityId}/gamedata`;
    const activityAssetsFolder = `${uploadsURL}/${activityAssetsKey}/`;
    const learnerAssetsKey =
      learnerId && `${usersKey}/${learnerId}/${activityId}`;
    const learnerAssetsFolder =
      learnerId && `${uploadsURL}/${learnerAssetsKey}/`;

    /*locale =
      (solutionLocale && solutionLocale.code) ||
      (multiLocaleGuides && multiLocaleGuides.defaultLocale) ||
      'en';*/

    const currentGuides = guides.length ? guides : multiLocaleGuides
      ? multiLocaleGuides[multiLocaleGuides.defaultLocale] : [];
    console.log('WritingAssistant currentGuides: ',currentGuides);
    
    const mapper = stringProblemsMapper({ translate, getStringAudio, translateArgs: ['', false, true, locale] });
    const filteredGuides = currentGuides
      .map((guide, idx) => ({
        ...(guide || {}),
        starter: starters[idx],
        text: guide?.string || guide?.text
      }))
      .filter((g) => !!(g.text || g.starter || g.audio))
      .map(mapper);
    console.log('WritingAssistant filteredGuides: ',filteredGuides);
    
    const filteredImages = images
      .filter((i) => !!i)
      .map((i) => activityAssetsFolder + i);
    const filteredWords = words.filter((w) => !!(w && w.trim()) && w);

    this.state = {
      paths: {
        activityAssetsKey,
        activityAssetsFolder,
        learnerAssetsKey,
        learnerAssetsFolder,
      },
      reading,
      activeGuideIdx: 0,
      filteredGuides,
      filteredImages,
      filteredWords,
      data: defaultData,
      learnersIDs,
      exitAnimationVisible: false,
    };

    if (props.mobileLayout && (this.mobilePlatform === 'android' || this.mobilePlatform === 'ios')) {
      this.mobileStableViewport = getViewportSize();
    }
  }

  componentDidMount() {
    document.addEventListener('startGame', this.init);
    this.bindMobileViewportSizing();

    const { learnersIDs } = this.state;

    // Get students data for this activity
    if (learnersIDs.length) {
      this.getLearnerData();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('startGame', this.init);
    this.unbindMobileViewportSizing();
    if (this.mobileViewportTimer) clearTimeout(this.mobileViewportTimer);
  }

  bindMobileViewportSizing = () => {
    if (!(this.mobilePlatform === 'android' || this.mobilePlatform === 'ios')) return;
    if (!this.props.mobileLayout || typeof window === 'undefined') return;

    if (!this.mobileStableViewport?.width || !this.mobileStableViewport?.height) {
      this.mobileStableViewport = getViewportSize();
    }

    window.addEventListener('resize', this.handleMobileViewportChange);
    window.visualViewport?.addEventListener('resize', this.handleMobileViewportChange);
  };

  unbindMobileViewportSizing = () => {
    if (typeof window === 'undefined') return;
    window.removeEventListener('resize', this.handleMobileViewportChange);
    window.visualViewport?.removeEventListener('resize', this.handleMobileViewportChange);
  };

  handleMobileViewportChange = () => {
    const viewport = getViewportSize();
    const stable = this.mobileStableViewport;

    if (!viewport.width || !viewport.height) return;

    if (!stable?.width || !stable?.height) {
      this.mobileStableViewport = viewport;
      this.forceUpdate();
      return;
    }

    const widthDelta = Math.abs(viewport.width - stable.width);
    const heightDelta = viewport.height - stable.height;
    const keyboardLikeResize = widthDelta < 24 && heightDelta < -80;

    // Native keyboard resize must not rescale or recenter the whole activity.
    if (keyboardLikeResize) return;

    if (widthDelta >= 24 || Math.abs(heightDelta) > 40) {
      clearTimeout(this.mobileViewportTimer);
      this.mobileViewportTimer = setTimeout(() => {
        this.mobileStableViewport = viewport;
        this.forceUpdate();
      }, 80);
    }
  };

  isNativePhoneRuntime = () => (
    (this.mobilePlatform === 'android' || this.mobilePlatform === 'ios')
    && isNativePhoneViewport(
      this.props.mobileLayout,
      this.mobileStableViewport || getViewportSize(),
    )
  );

  onExit = (cb) => {
    const {
      preview,
      setActivityMastered,
      setActivityPartial,
      closeWritingFeedback,
    } = this.props;

    if (this.state.exiting) return;

    closeWritingFeedback();

    if (preview) {
      return cb && cb();
    }

    const text = this.editorRef.current.getText();
    const regExpResult = text.match(/[\w)](\s+)?[.?!]+/g);
    const desktopSentencesCount = regExpResult ? regExpResult.length : 0;

    if (!this.isNativePhoneRuntime()) {
      // Preserve the existing desktop result/exit flow exactly.
      this.setState({ exiting: true }, () => {
        if (desktopSentencesCount >= 3) {
          setActivityMastered();
        } else if (desktopSentencesCount > 0) {
          setActivityPartial();
        }

        this.saveChanges(cb);
      });
      return;
    }

    const sentencesCount = countWritingSentences(text);

    // Leaving an empty Writing activity is navigation, not submission.
    // Match Online Play: do not show Results or award a partial/yellow star.
    if (sentencesCount === 0) {
      this.setState({ exiting: true }, () => this.saveChanges(cb));
      return;
    }

    this.exitResult = {
      mastered: sentencesCount >= 3,
      submittedText: text.trim(),
    };

    this.setState({ exiting: true }, () => {
      this.saveChanges(() => {
        // A partial Writing result must not display a success animation.
        if (this.exitResult?.mastered) {
          this.setState({ exitAnimationVisible: true });
        } else {
          this.finishAnimatedExit();
        }
      });
    });
  };

  finishAnimatedExit = () => {
    const { showStats } = this.props;
    if (!this.exitResult) return;

    const { mastered: predefinedMastered, submittedText } = this.exitResult;
    this.exitResult = null;
    this.setState({ exitAnimationVisible: false }, () => {
      showStats({
        predefinedMastered,
        compactMobile: true,
        skipCharacterAnimation: true,
        data: {
          rawAssessment: {
            submittedText,
            textLength: submittedText.length,
          },
        },
      });
    });
  };

  getLearnerData = async () => {
    const {
      activity: {
        activity: { _id: activityId },
      },
      noReports
    } = this.props;
    const { learnersIDs } = this.state;

    if (!learnersIDs.length || noReports) return;

    const learnersData = await Promise.all(
      learnersIDs.map((learnerId) =>
        API.game.getLearnerActivityData({
          learnerId,
          activityId,
        })
      )
    );

    // If multiple members logged in, then check that they have same activity data
    const allDataIsTheSame = !!learnersData.reduce(
      (a, b) => JSON.stringify(a) === JSON.stringify(b)
    );

    if (!allDataIsTheSame) return;

    const data = learnersData[0];
    if (!data) return;

    this.editorRef.current.loadHtml(data.text);

    this.setState({
      data,
    });
  };

  onEditorChange = (html) => {
    const { data = {} } = this.state;

    if (data.text === html) return;

    this.setState({
      hasUnsavedChanges: true,
      data: {
        ...data,
        text: html,
      },
    });
  };

  saveChanges = (cb) => {
    const { activity, learnerId, preview, noReports } = this.props;
    const { data, hasUnsavedChanges, exiting, learnersIDs } = this.state;

    if (preview || !learnerId || !hasUnsavedChanges || noReports) {
      return cb && cb();
    }

    API.game
      .saveLearnerActivityData({
        learnerId: learnersIDs,
        activityId: activity.activity._id,
        data,
      })
      .then((success) => {
        success && !exiting && this.setState({ hasUnsavedChanges: false });
        cb && cb();
      })
      .catch((err) => {
        console.log(err);
        cb && cb();
      });
  };

  onChangeActiveGuide = (idx) => {
    stopAudio();
    this.setState({ activeGuideIdx: idx });
  };

  playGuideAudio = () => {
    const {
      paths: { activityAssetsFolder },
    } = this.state;

    const current = this.getCurrentGuide();
    if (!current || !current.audio) return;

    toggleAudio((current.audioPath || activityAssetsFolder) + current.audio);
  };

  getCurrentGuide = () => {
    const { filteredGuides, activeGuideIdx } = this.state;
    const currentGuide = (filteredGuides || [])[activeGuideIdx];
console.log('WritingAssistant CurrentGuide: ',[currentGuide, activeGuideIdx]);
    return currentGuide;
  };

  onPasteStarter = (html) => {
    const editor = this.editorRef.current;
    if (!editor) return;

    editor.pasteHtml(html);
  };

  print = () => {
    const { data } = this.state;
    const div = document.createElement('div');
    div.style.width = EDITOR_WIDTH + 'px';
    div.style.margin = '0 auto';
    div.innerHTML = data.text;

    printElem(div);
  };

  editorRef = React.createRef();

  render() {
    const {
      data,
      reading,
      exiting,
      activeGuideIdx,
      filteredGuides,
      filteredWords,
      filteredImages,
      exitAnimationVisible,
    } = this.state;

    const currentGuide = this.getCurrentGuide();
    const { mobileLayout = false } = this.props;
    const mobilePlatform = mobileLayout ? this.mobilePlatform : 'web';
    const mobileStableViewport = this.mobileStableViewport;
    const nativePhoneViewport = isNativePhoneViewport(mobileLayout, mobileStableViewport);
    const writingScale = getWritingScale(mobileLayout, mobileStableViewport, mobilePlatform);
    const writingTop = nativePhoneViewport
      ? getWritingTop(mobileStableViewport || getViewportSize(), mobilePlatform, writingScale)
      : undefined;
    const writingHeight = nativePhoneViewport ? divideSize(WRITING_PHONE_HEIGHT) : divideSize(1280);
    const editorOuterWidth = nativePhoneViewport
      ? divideSize(WRITING_PHONE_EDITOR_WIDTH)
      : divideSize(1400);
    // const { translate } = this.props;

    return (
      <>
        {exitAnimationVisible && (
          <CharacterAnimation
            onFinish={this.finishAnimatedExit}
            onError={this.finishAnimatedExit}
          />
        )}

        <div
          style={{
            width: divideSize(2000),
            height: writingHeight,
            top: writingTop,
            transform: 'translate(-50%, -50%) scale(' + writingScale + ')',
          }}
          className={classnames(
            classes.wrapper,
            'writing-assistant-mobile-wrapper',
            nativePhoneViewport && `writing-assistant-platform-${mobilePlatform}`,
            { [classes.noGuide]: !filteredGuides.length },
          )}
        >
          {filteredGuides.length !== 0 && (
            <WhiteBox
              outerStyle={{
                width: divideSize(620),
                fontSize: '14px',
                padding: '15px',
                borderRadius: '30px',
              }}
              innerClass={classes.guideInner}
            >
              <GuideBox
                templateGraphicsFolder={templateGraphicsFolder}
                activeGuideIdx={activeGuideIdx}
                guides={filteredGuides}
                onPasteStarter={this.onPasteStarter}
                onChangeActive={this.onChangeActiveGuide}
              />
            </WhiteBox>
          )}

          <WhiteBox
            outerStyle={{
              width: editorOuterWidth,
              fontSize: '20px',
              padding: '15px',
              borderRadius: '30px',
            }}
            innerClass={classes.textInner}
          >
            <WritingAssistantEditor
              reading={reading}
              initialValue={data && data.text}
              saveChanges={this.saveChanges}
              ref={this.editorRef}
              words={filteredWords}
              images={filteredImages}
              changeHandler={this.onEditorChange}
              writingFeedbackEnabled={!nativePhoneViewport}
              preventProgrammaticFocus={nativePhoneViewport}
            />
          </WhiteBox>
        </div>

        {!nativePhoneViewport && <WritingFeedback />}

        <ActivityButtons
          buttons={[
            {
              type: GO_BACK,
              beforeClickHandler: this.onExit,
              disabled: exiting,
            },
            {
              type: PRINT,
              onClick: this.print,
            },
            {
              type: SPEAK,
              onClick: this.playGuideAudio,
              dontShow: !currentGuide || !currentGuide.audio,
            },
            HELP,
          ]}
        />
      </>
    );
  }
}

WritingAssistant.propTypes = {
  solutionLocale: PropTypes.object,
  learnerId: PropTypes.string,
  setActivityPartial: PropTypes.func.isRequired,
  setActivityMastered: PropTypes.func.isRequired,
  noReports: PropTypes.bool,
  mobileLayout: PropTypes.bool,
  showStats: PropTypes.func.isRequired,
};

const mapStateToProps = ({ flinkPlay: { learner, team, learningCenter } }) => ({
  learnerId: learner && learner._id,
  noReports: !!(
    learningCenter &&
    learningCenter.config &&
    learningCenter.config.noReports
  ),
  team,
});

export default connect(mapStateToProps, {
  setActivityPartial,
  setActivityMastered,
  closeWritingFeedback,
  showStats,
})(WritingAssistant);
